"use strict";
/**
 * Copyright 2021 Angus.Fenying <fenying@litert.org>
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.equals = exports.identify = exports.createExceptionRegistry = exports.E_DUP_EXCEPTION_CODE = exports.E_DUP_EXCEPTION_NAME = exports.E_MALFORMED_NAME = exports.E_MALFORMED_MODULE = exports.E_MALFORMED_TYPE = exports.E_TYPE_NOT_FOUND = void 0;
var U = require("./Utilites");
var BUILT_IN_TYPE_NAME = 'built_in';
var BUILT_IN_ERR_TYPE_NOT_FOUND = 'type_not_found';
var BUILT_IN_ERR_MALFORMED_TYPE = 'malformed_type';
var BUILT_IN_ERR_MALFORMED_NAME = 'malformed_name';
var BUILT_IN_ERR_MALFORMED_MODULE = 'malformed_module';
var BUILT_IN_ERR_DUP_EXCEPTION_NAME = 'dup_exception_name';
var BUILT_IN_ERR_DUP_EXCEPTION_CODE = 'dup_exception_code';
var VALIDATE_TYPE = /^[_a-z0-9]+$/;
var VALIDATE_MODULE = /^[-a-z0-9]+(\.[-a-z0-9]+)*$/;
var VALIDATE_NAME = /^[_a-z0-9]+$/;
var URL_PROTOCOL = 'exception:';
var BaseException = /** @class */ (function () {
    function BaseException(metadata, origin, theCtor) {
        this.metadata = __assign(__assign({}, this.constructor.metadata), metadata);
        this.origin = origin;
        switch (typeof theCtor) {
            default:
            case 'function':
                U.captureStackTrace(this, theCtor !== null && theCtor !== void 0 ? theCtor : this.constructor);
                break;
            case 'string':
                this.stack = theCtor;
                break;
        }
    }
    BaseException.prototype.toString = function () {
        var _a;
        var ret = URL_PROTOCOL + "//" + this.module + "/" + this.type + "/" + this.name + "?msg=" + encodeURIComponent(this.message) + (typeof this.code === 'number' && "&code=" + this.code) + "&stack=" + encodeURIComponent(this.stack) + "&meta=" + encodeURIComponent(JSON.stringify(this.metadata)) + "&origin=" + encodeURIComponent(JSON.stringify((_a = this.origin) !== null && _a !== void 0 ? _a : null));
        return ret;
    };
    BaseException.prototype.toJSON = function () {
        return {
            code: this.code,
            name: this.name,
            message: this.message,
            stack: this.stack,
            origin: this.origin,
            metadata: this.metadata,
            module: this.module,
            type: this.type,
        };
    };
    return BaseException;
}());
var ExceptionRegistry = /** @class */ (function () {
    function ExceptionRegistry(opts) {
        this._types = {};
        this._codeIndex = {};
        this._nameIndex = {};
        this._module = opts.module;
        if (!VALIDATE_MODULE.test(opts.module) || !U.notStartOrEndWith(opts.module, '-')) {
            throw _reg().raiseError(BUILT_IN_ERR_MALFORMED_MODULE, { 'module': opts.module });
        }
        for (var t in opts.types) {
            if (!VALIDATE_TYPE.exec(t) || !U.notStartOrEndWith(t, '_')) {
                throw _reg().raiseError(BUILT_IN_ERR_MALFORMED_TYPE, { 'type': t });
            }
            var typ = opts.types[t];
            this._types[t] = typeof typ === 'boolean' ? {} : {
                'indexFn': typ.index
            };
        }
    }
    Object.defineProperty(ExceptionRegistry.prototype, "module", {
        get: function () {
            return this._module;
        },
        enumerable: false,
        configurable: true
    });
    ExceptionRegistry.prototype.raiseError = function (name, metadata, origin) {
        var ctor = this._nameIndex[name.toLowerCase()];
        // eslint-disable-next-line @typescript-eslint/unbound-method
        return new ctor(metadata, origin, this.raiseError);
    };
    ExceptionRegistry.prototype.has = function (id) {
        return !!(typeof id === 'string' ? this._nameIndex[id] : this._codeIndex[id]);
    };
    ExceptionRegistry.prototype.get = function (id) {
        var _a;
        return (_a = (typeof id === 'string' ? this._nameIndex[id] : this._codeIndex[id])) !== null && _a !== void 0 ? _a : null;
    };
    ExceptionRegistry.prototype.register = function (opts) {
        var _a, _b, _c;
        var type = this._types[opts.type];
        if (!type) {
            throw _reg().raiseError(BUILT_IN_ERR_TYPE_NOT_FOUND, __assign(__assign({}, opts), { 'module': this._module }));
        }
        if (!VALIDATE_NAME.test(opts.name) || !U.notStartOrEndWith(opts.name, '_')) {
            throw _reg().raiseError(BUILT_IN_ERR_MALFORMED_NAME, __assign(__assign({}, opts), { 'module': this._module }));
        }
        var code = (_a = opts.code) !== null && _a !== void 0 ? _a : (_c = (_b = this._types[opts.type]).indexFn) === null || _c === void 0 ? void 0 : _c.call(_b);
        if (this._nameIndex[opts.name]) {
            throw _reg().raiseError(BUILT_IN_ERR_DUP_EXCEPTION_NAME, __assign(__assign({}, opts), { 'module': this._module }));
        }
        if (typeof code === 'number' && this._codeIndex[code]) {
            throw _reg().raiseError(BUILT_IN_ERR_DUP_EXCEPTION_NAME, __assign(__assign({}, opts), { 'module': this._module }));
        }
        var ctor = (new Function('BaseException', "class " + opts.name + " extends BaseException { } return " + opts.name + ";"))(BaseException);
        Object.defineProperties(ctor, {
            'message': {
                'writable': false,
                'configurable': false,
                'value': opts.message
            },
            'code': {
                'writable': false,
                'configurable': false,
                'value': code
            },
            'name': {
                'writable': false,
                'configurable': false,
                'value': opts.name
            },
            'module': {
                'writable': false,
                'configurable': false,
                'value': this.module
            },
            'type': {
                'writable': false,
                'configurable': false,
                'value': opts.type
            },
            'metadata': {
                'writable': false,
                'configurable': false,
                'value': opts.metadata
            }
        });
        Object.defineProperties(ctor.prototype, {
            'message': {
                'writable': false,
                'configurable': false,
                'value': opts.message
            },
            'code': {
                'writable': false,
                'configurable': false,
                'value': code
            },
            'name': {
                'writable': false,
                'configurable': false,
                'value': opts.name
            },
            'module': {
                'writable': false,
                'configurable': false,
                'value': this.module
            },
            'type': {
                'writable': false,
                'configurable': false,
                'value': opts.type
            }
        });
        this._nameIndex[opts.name] = ctor;
        if (typeof code === 'number') {
            this._codeIndex[code] = ctor;
        }
        return ctor;
    };
    ExceptionRegistry.prototype.identify = function (e, type, name) {
        return e instanceof BaseException && (!type || (this._module === e.module && type === e.type && (!name || name === e.name)));
    };
    ExceptionRegistry.prototype.parse = function (e) {
        var _a, _b, _c, _d, _e;
        try {
            var url = new URL(e);
            if (url.protocol !== URL_PROTOCOL) {
                return null;
            }
            var _f = url.pathname.split('/'), type = _f[1], eName = _f[2];
            if (!((_a = url.searchParams) === null || _a === void 0 ? void 0 : _a.has('code'))
                || !((_b = url.searchParams) === null || _b === void 0 ? void 0 : _b.has('msg'))
                || !((_c = url.searchParams) === null || _c === void 0 ? void 0 : _c.has('stack'))
                || !((_d = url.searchParams) === null || _d === void 0 ? void 0 : _d.has('meta'))
                || !((_e = url.searchParams) === null || _e === void 0 ? void 0 : _e.has('origin'))) {
                return null;
            }
            if (this._nameIndex[eName] && url.hostname === this._module) {
                return new this._nameIndex[eName](JSON.parse(url.searchParams.get('meta')), JSON.parse(url.searchParams.get('origin')), 
                // eslint-disable-next-line @typescript-eslint/unbound-method
                url.searchParams.get('stack'));
            }
            var ret = new BaseException(JSON.parse(url.searchParams.get('meta')), JSON.parse(url.searchParams.get('origin')));
            ret.stack = url.searchParams.get('stack');
            ret.message = url.searchParams.get('msg');
            ret.module = url.hostname;
            ret.type = type;
            ret.name = eName;
            ret.code = parseInt(url.searchParams.get('code'));
            return ret;
        }
        catch (_g) {
            return null;
        }
    };
    ExceptionRegistry.prototype.getDefinitions = function () {
        return Object.values(this._codeIndex).map(function (v) { return ({
            'name': v.name,
            'code': v.code,
            'module': v.module,
            'message': v.message,
            'type': v.type,
            'metadata': __assign({}, v.metadata)
        }); });
    };
    return ExceptionRegistry;
}());
var internalReg = new ExceptionRegistry({
    'module': 'exceptions.litert.org',
    'types': (_a = {},
        _a[BUILT_IN_TYPE_NAME] = {
            'index': (function () {
                var index = -0xFFFFFFFF;
                return function () { return index++; };
            })(),
        },
        _a)
});
/**
 * Built-in exception: The type of new exception definition is not defined.
 */
exports.E_TYPE_NOT_FOUND = internalReg.register({
    'name': BUILT_IN_ERR_TYPE_NOT_FOUND,
    'message': 'The type of new exception definition is not defined.',
    'metadata': {},
    'type': BUILT_IN_TYPE_NAME
});
/**
 * Built-in exception: The type of new exception definition is malformed.
 */
exports.E_MALFORMED_TYPE = internalReg.register({
    'name': BUILT_IN_ERR_MALFORMED_TYPE,
    'message': 'The type of new exception definition is malformed.',
    'metadata': {},
    'type': BUILT_IN_TYPE_NAME
});
/**
 * Built-in exception: The module name of new exception registry is malformed.
 */
exports.E_MALFORMED_MODULE = internalReg.register({
    'name': BUILT_IN_ERR_MALFORMED_MODULE,
    'message': 'The module name of new exception registry is malformed.',
    'metadata': {},
    'type': BUILT_IN_TYPE_NAME
});
/**
 * Built-in exception: The name of new exception is malformed.
 */
exports.E_MALFORMED_NAME = internalReg.register({
    'name': BUILT_IN_ERR_MALFORMED_NAME,
    'message': 'The name of new exception is malformed.',
    'metadata': {},
    'type': BUILT_IN_TYPE_NAME
});
/**
 * Built-in exception: The name of new exception has been used already.
 */
exports.E_DUP_EXCEPTION_NAME = internalReg.register({
    'name': BUILT_IN_ERR_DUP_EXCEPTION_NAME,
    'message': 'The name of new exception has been used already.',
    'metadata': {},
    'type': BUILT_IN_TYPE_NAME
});
/**
 * Built-in exception: The code of new exception has been used already.
 */
exports.E_DUP_EXCEPTION_CODE = internalReg.register({
    'name': BUILT_IN_ERR_DUP_EXCEPTION_CODE,
    'message': 'The code of new exception has been used already.',
    'metadata': {},
    'type': BUILT_IN_TYPE_NAME
});
// eslint-disable-next-line @typescript-eslint/naming-convention
function _reg() {
    return internalReg;
}
/**
 * Create a new exception registry object.
 * @param opts  The options of new registry.
 */
function createExceptionRegistry(opts) {
    return new ExceptionRegistry(opts);
}
exports.createExceptionRegistry = createExceptionRegistry;
/**
 * Helper function that identifies whether a value is a determined exception object.
 * @param e         The value to be identified.
 * @param type      The expected exception type, optional.
 * @param name      The expected exception name, optional.
 * @param module    The expected exception module name, optional.
 */
function identify(e, type, name, module) {
    return e instanceof BaseException && (!type || (type === e.type && (!name || name === e.name) && (!module || module === e.module)));
}
exports.identify = identify;
/**
 * Helper function that identifies whether a value is a determined exception object.
 * @param e             The value to be identified.
 * @param exceptionCtor The constructor of expected exception.
 */
function equals(e, exceptionCtor) {
    return e instanceof exceptionCtor || (e instanceof BaseException
        && exceptionCtor.type === e.type
        && exceptionCtor.name === e.name
        && exceptionCtor.module === e.module);
}
exports.equals = equals;
//# sourceMappingURL=Register.js.map